import type {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'
import { QueryClient } from '@tanstack/react-query'
import { del, get, set } from 'idb-keyval'

let BACKEND_URL = 'http://localhost:3001'

if (process.env.NEXT_PUBLIC_BACKEND_URL) {
  BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        // Data can be fresh or stale, and staleTime defines when it goes stale.
        // When the data is marked as stale, once the user comes back to the page, the data will be refetched.
        staleTime: 1000 * 60 * 60 * 24,
        retry: 1,
        // Keep unused data for infinity amount of time (Garbage collect Time)
        // We keep the data forever to get the email address of the user in the sidebar as we don't have a request in the backend to get it
        gcTime: Infinity, // Keep data forever - must be >= staleTime to prevent premature cache eviction
        // Disable automatic interval fetching
        refetchInterval: 0,
        refetchIntervalInBackground: false,
        refetchOnMount: true,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient()
  }
  else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // supsends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient)
      browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}

// In RTK Query we can find tagTypes in createAPI function.
// In order to have a single place to check the tagTypes we will use this object

const TAG_TYPES = {
  AUTH: 'AUTH',
  USERS: 'USERS',
  USER: 'USER',
  DESIGNS: 'Designs',
  SIM_CONFIG: 'SimConfig',
  CART: 'Cart',
  PAYMENT: 'Payment',
  SELECTORS: 'Selectors',
  ORDER: 'Order',
  TRANSPORT: 'Transport',
  BUDGET: 'Budget',
  ACTIVITIES: 'Activities',
  CATALOG: 'Catalog',
  PLANTERS: 'Planters',
  LATTICES: 'Lattices',
  ZELLIGES: 'Zelliges',
  BARRO_ALTA: 'BarroAlta',
  MUESTRAS: 'Muestras',
} as const

function createIDBPersister(idbValidKey: IDBValidKey) {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client)
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey)
    },
    removeClient: async () => {
      await del(idbValidKey)
    },
  } as Persister
}

const persister = createIDBPersister('queryClient.v1')

export { BACKEND_URL, getQueryClient, persister, TAG_TYPES }
